/**
 * Website visitors, link clicks and paths (owner, 2026-10-13: "as long as we track visitors … and link clicks … and paths").
 * Pure parts only; the browser side is apps/landing/site/consent.js (the view and its referrer) and site/track.js (clicks, paths),
 * which repeat siteTemplate and the control names below (a landing test keeps them equal).
 *
 *  ANONYMOUS, everyone, no ask (core compliance/measure.ts): a page view carries where the visit came from as a DOMAIN only
 *  ("google.com", never the address or its query) plus utm_source / utm_campaign; a tap carries a stable control name. Counts only.
 *  LINKED, only with the cookie banner's Analytics yes: a random visit id kept in sessionStorage (this tab, gone when it closes)
 *  joins the pages of one visit, so the dashboard can show paths, entry and exit pages and drop-off before "Start your 25 days free".
 *  A visit is never joined to an account: "sign-up" here means the visit tapped the sign-up button.
 */
import { STATE_INDEX } from "./states/bundles/eager-facts.ts";
import { cleanUtm } from "./siteanalytics.ts";

/* ─────────────── where a visit came from ─────────────── */

/** A plain host name: letters, digits and hyphens in dotted labels, a letter TLD (so never an IP address), at most 64 characters. */
const HOST = /^(?=.{1,64}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/;
/** The value a page view sends for a visit with no referrer (typed, bookmarked, an app that hides it). */
export const DIRECT = "(direct)";

/**
 * The referrer as a domain, or null. Takes ONLY a bare host name ("www.Google.com" -> "google.com"); a URL, a path, a query, a port,
 * a user name or an IP address is refused (the browser sends the domain only; anything more is not ours to keep).
 */
export function refDomain(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const h = raw.trim().toLowerCase().replace(/^(?:www\d?|m)\./, "");
  return HOST.test(h) ? h : null;
}

/** AI answer engines, by the domains their links come from (a utm_source of the same name counts too: ChatGPT adds one). */
export const AI_ENGINES: Record<string, string> = {
  "chatgpt.com": "ChatGPT", "chat.openai.com": "ChatGPT", "openai.com": "ChatGPT", "perplexity.ai": "Perplexity", "claude.ai": "Claude",
  "gemini.google.com": "Gemini", "bard.google.com": "Gemini", "copilot.microsoft.com": "Copilot", "meta.ai": "Meta AI", "grok.com": "Grok",
  "you.com": "You.com", "phind.com": "Phind", "chat.deepseek.com": "DeepSeek", "deepseek.com": "DeepSeek", "chat.mistral.ai": "Le Chat",
};
/** The AI engine a domain (or utm_source value) belongs to, or "". */
export function aiEngine(domain: string): string {
  const d = domain.toLowerCase();
  for (const [k, v] of Object.entries(AI_ENGINES)) if (d === k || d.endsWith(`.${k}`)) return v;
  return "";
}

/** utm_campaign, cleaned like utm_source (core cleanUtm): lowercased, short and plain, else "". */
export function cleanCampaign(v: unknown): string {
  if (typeof v !== "string") return "";
  const s = v.trim().toLowerCase();
  return /^[a-z0-9][a-z0-9._-]{0,39}$/.test(s) && s !== "other" ? s : ""; // "other" is the server's overflow bucket, never a sent value
}

/**
 * Where a page view came from, read from POST /measure/anon's site view (beside the event, never inside it: the count row has no
 * room for it). ref "" = no referrer (DIRECT); a domain = from another site; absent = a page reached from the site itself (not a
 * visit). A ref that is not a bare domain (a full URL, a query) is dropped, never cut down. utm values as cleanUtm / cleanCampaign.
 */
export function viewSources(raw: Record<string, unknown>): { ref: string | null; utm: string; campaign: string } {
  const ref = raw.ref === "" ? DIRECT : refDomain(raw.ref);
  return { ref, utm: ref ? cleanUtm(raw.utm) : "", campaign: ref ? cleanCampaign(raw.cmp) : "" };
}

/* ─────────────── what a click is called ─────────────── */

/** The main buttons, by a fixed name (never their text, never a phone number). */
export const SITE_CTAS = {
  "start-free": "Start your 25 days free", "see-it-work": "See it work", call: "Call", text: "Text us", email: "Email", "sign-in": "Sign in",
} as const;
export type SiteCta = keyof typeof SITE_CTAS;

const STATE_SLUGS = new Set(Object.values(STATE_INDEX).map((s) => s.slug));
/**
 * A site page as a template, so a click's name is the kind of page it goes to, not one of thousands: the first part stays
 * ("/blog/"), a state's slug becomes ":state" and keeps the part after it ("/:state/blog/"), every deeper part is ":slug"
 * ("/blog/:slug/", "/find/:slug/:slug/"). Four parts at most. Null for anything that is not a plain site path.
 */
export function siteTemplate(path: string): string | null {
  const p = String(path).split(/[?#]/)[0]!.replace(/index\.html$/, "");
  if (!p.startsWith("/")) return null;
  const segs = p.split("/").filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < segs.length && i < 4; i++) {
    const s = segs[i]!;
    if (i === 0) { if (s !== ":state" && !/^[a-z0-9][a-z0-9-]{0,39}$/.test(s)) return null; out.push(STATE_SLUGS.has(s) ? ":state" : s); } // a template is its own template
    else if (i === 1 && out[0] === ":state") { if (!/^[a-z0-9][a-z0-9-]{0,39}$/.test(s)) return null; out.push(s); }
    else out.push(":slug");
  }
  return out.length ? `/${out.join("/")}/` : "/";
}

/**
 * The shape of a site click's name: cta:<fixed>, to:<template>, out:<domain>, cc:<data-cc name>, id:<element id>. 60 characters
 * at most (the count's column). The server also checks that a to: template is a page the site has and bounds the free names.
 */
export function siteControlShape(raw: unknown): string | null {
  if (typeof raw !== "string" || raw.length > 60) return null;
  const i = raw.indexOf(":");
  if (i <= 0) return null;
  const kind = raw.slice(0, i), rest = raw.slice(i + 1);
  switch (kind) {
    case "cta": return Object.hasOwn(SITE_CTAS, rest) ? raw : null; // own keys only: never "__proto__" or "constructor"
    case "to": return siteTemplate(rest) === rest ? raw : null;
    case "out": return refDomain(rest) === rest ? raw : null;
    case "cc": return /^[a-z0-9][a-z0-9-]{0,39}$/.test(rest) && rest !== "other" ? raw : null;
    case "id": return /^[a-z][a-z0-9_-]{0,39}$/i.test(rest) && rest.toLowerCase() !== "other" ? `id:${rest.toLowerCase()}` : null;
    default: return null;
  }
}

/* ─────────────── a linked visit (consented only) ─────────────── */

/** The visit id: 32 hex characters made by the browser at random; nothing else about the visitor. */
export const VISIT_ID = /^[0-9a-f]{32}$/;
/** The pages one visit keeps (a longer visit keeps its first ones). */
export const MAX_VISIT_PAGES = 30;
/** Linked visits are kept this long, then deleted (siteanalytics retention timer). */
export const VISIT_KEEP_DAYS = 90;

export interface VisitPing { visit: string; path: string; ref: string | null; signup: boolean }
/** POST /measure/visit's body, checked strictly: { v: id, p: path, r?: domain (the first page only), s?: true (tapped sign-up) }. */
export function parseVisitPing(raw: unknown): VisitPing | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  if (Object.keys(o).some((k) => !["v", "p", "r", "s"].includes(k))) return null;
  if (typeof o.v !== "string" || !VISIT_ID.test(o.v)) return null;
  if (typeof o.p !== "string" || o.p.length > 200 || !/^\/(?:[a-z0-9][a-z0-9-]{0,79}\/){0,6}$/.test(o.p)) return null;
  if (o.s !== undefined && o.s !== true) return null;
  const ref = o.r === undefined ? null : o.r === "" ? DIRECT : refDomain(o.r);
  if (o.r !== undefined && ref === null) return null;
  return { visit: o.v, path: o.p, ref, signup: o.s === true };
}

export interface SiteVisit { id: string; day: string; ref: string; pages: string[]; signup: boolean }

/* ─────────────── the Website report ─────────────── */

export interface AnonRow { day: string; surface: string; kind: string; path: string; control: string; n: number }
export interface SourceRow { day: string; dim: "ref" | "utm_source" | "utm_campaign"; value: string; n: number }

/**
 * What the Website view reads, already added up by the store (in SQL for Postgres; security run-10 F1/F2: never a raw, capped
 * list of rows, so nothing is silently cut): the site's views per day, its top pages and click names.
 */
export interface AnonSiteSummary { days: { day: string; views: number }[]; pages: { path: string; views: number }[]; taps: { control: string; n: number }[] }
/** Anonymous visits per day (the ref counts) and the top values of each source kind. */
export interface SourceSummary { days: { day: string; visits: number }[]; top: { dim: SourceRow["dim"]; value: string; n: number }[] }
/** The consented visits that started in the range, added up: totals, 5-page path prefixes, entry and exit pages. */
export interface VisitSummary {
  visits: number; signups: number;
  paths: { path: string[]; more: boolean; signup: boolean; visits: number }[];
  entries: { path: string; visits: number; signups: number }[];
  exits: { path: string; visits: number }[];
}
/** How many top rows each summary keeps (the store's SQL uses the same). */
export const SUMMARY_TOP = { pages: 30, taps: 200, sources: 100, paths: 20, entries: 20, exits: 20 } as const;

const topN = <T,>(m: Map<string, T>, by: (t: T) => number, n: number) => [...m.entries()].sort((a, b) => by(b[1]) - by(a[1]) || (a[0] < b[0] ? -1 : 1)).slice(0, n);
const add = (m: Map<string, number>, k: string, n = 1) => m.set(k, (m.get(k) ?? 0) + n);

/** The anonymous site summary from raw count rows (the memory store; Postgres does the same in SQL). */
export function summarizeAnonSite(rows: readonly AnonRow[], from: string, to: string): AnonSiteSummary {
  const days = new Map<string, number>(), pages = new Map<string, number>(), taps = new Map<string, number>();
  for (const r of rows) {
    if (r.surface !== "site" || r.day < from || r.day > to) continue;
    if (r.kind === "view") { add(days, r.day, r.n); add(pages, r.path, r.n); }
    else if (r.kind === "tap" && r.control) add(taps, r.control, r.n);
  }
  return {
    days: [...days].map(([day, views]) => ({ day, views })),
    pages: topN(pages, (n) => n, SUMMARY_TOP.pages).map(([path, views]) => ({ path, views })),
    taps: topN(taps, (n) => n, SUMMARY_TOP.taps).map(([control, n]) => ({ control, n })),
  };
}
/** The source summary from raw rows. */
export function summarizeSources(rows: readonly SourceRow[], from: string, to: string): SourceSummary {
  const days = new Map<string, number>(), vals = new Map<string, number>();
  for (const r of rows) {
    if (r.day < from || r.day > to) continue;
    if (r.dim === "ref") add(days, r.day, r.n);
    add(vals, `${r.dim}|${r.value}`, r.n);
  }
  const per = new Map<string, number>(), top: SourceSummary["top"] = [];
  for (const [k, n] of topN(vals, (n) => n, Infinity)) {
    const [dim, value] = [k.slice(0, k.indexOf("|")), k.slice(k.indexOf("|") + 1)];
    if ((per.get(dim) ?? 0) >= SUMMARY_TOP.sources) continue;
    add(per, dim); top.push({ dim: dim as SourceRow["dim"], value, n });
  }
  return { days: [...days].map(([day, visits]) => ({ day, visits })), top };
}
/** The visit summary from whole visits. */
export function summarizeVisits(visits: readonly SiteVisit[], from: string, to: string): VisitSummary {
  const mine = visits.filter((v) => v.day >= from && v.day <= to && v.pages.length);
  const paths = new Map<string, VisitSummary["paths"][number]>(), entries = new Map<string, { visits: number; signups: number }>(), exits = new Map<string, number>();
  for (const v of mine) {
    const p = v.pages.slice(0, 5), more = v.pages.length > 5, k = `${p.join(" ")}|${more}|${v.signup}`;
    const row = paths.get(k) ?? { path: p, more, signup: v.signup, visits: 0 };
    row.visits++; paths.set(k, row);
    const e = entries.get(v.pages[0]!) ?? { visits: 0, signups: 0 };
    e.visits++; if (v.signup) e.signups++; entries.set(v.pages[0]!, e);
    if (!v.signup) add(exits, v.pages.at(-1)!);
  }
  return {
    visits: mine.length, signups: mine.filter((v) => v.signup).length,
    paths: topN(paths, (r) => r.visits, SUMMARY_TOP.paths).map(([, r]) => r),
    entries: topN(entries, (r) => r.visits, SUMMARY_TOP.entries).map(([path, r]) => ({ path, ...r })),
    exits: topN(exits, (n) => n, SUMMARY_TOP.exits).map(([path, n]) => ({ path, visits: n })),
  };
}

export interface WebsiteReport {
  from: string; to: string;
  /** Anonymous counts: every visitor except GPC / Do Not Track. */
  anonymous: {
    views: number; visits: number;
    days: { day: string; views: number; visits: number }[];
    pages: { path: string; views: number }[];
    referrers: { domain: string; visits: number; ai: string }[];
    ai: { engine: string; visits: number }[];
    utmSources: { value: string; visits: number; ai: string }[];
    utmCampaigns: { value: string; visits: number }[];
    ctas: { cta: SiteCta; label: string; taps: number }[];
    links: { control: string; taps: number }[];
  };
  /** From visitors who allowed Analytics: share = their visits over all anonymous visits (null with no visits). */
  linked: {
    visits: number; share: number | null; signups: number; signupRate: number | null;
    paths: { path: string[]; signup: boolean; visits: number }[];
    entries: { path: string; visits: number; signups: number }[];
    exits: { path: string; visits: number }[];
  };
}

const pct = (n: number, of: number): number | null => (of > 0 ? Math.round((n / of) * 1000) / 10 : null);

/** The Website view from the stores' summaries over [from, to] (`days`: every day in the range, zeros included). */
export function websiteReport(anon: AnonSiteSummary, sources: SourceSummary, linked: VisitSummary, from: string, to: string, days: string[]): WebsiteReport {
  const perDay = new Map(days.map((d) => [d, { day: d, views: 0, visits: 0 }]));
  for (const d of anon.days) { const x = perDay.get(d.day); if (x) x.views += d.views; }
  for (const d of sources.days) { const x = perDay.get(d.day); if (x) x.visits += d.visits; }
  const ctas: WebsiteReport["anonymous"]["ctas"] = [], links: WebsiteReport["anonymous"]["links"] = [];
  for (const t of anon.taps) {
    if (t.control.startsWith("cta:")) { const k = t.control.slice(4); if (Object.hasOwn(SITE_CTAS, k) && ctas.length < 20) ctas.push({ cta: k as SiteCta, label: SITE_CTAS[k as SiteCta], taps: t.n }); }
    else if (links.length < 30) links.push({ control: t.control, taps: t.n });
  }
  const of = (dim: SourceRow["dim"]) => sources.top.filter((r) => r.dim === dim);
  const ai = new Map<string, number>();
  for (const r of of("ref")) { const e = aiEngine(r.value); if (e) ai.set(e, (ai.get(e) ?? 0) + r.n); }
  const anonVisits = sources.days.reduce((a, d) => a + d.visits, 0);
  return {
    from, to,
    anonymous: {
      views: anon.days.reduce((a, d) => a + d.views, 0), visits: anonVisits,
      days: [...perDay.values()],
      pages: anon.pages.slice(0, 30),
      referrers: of("ref").slice(0, 30).map((r) => ({ domain: r.value, visits: r.n, ai: aiEngine(r.value) })),
      ai: [...ai].sort((a, b) => b[1] - a[1]).map(([engine, visits]) => ({ engine, visits })),
      utmSources: of("utm_source").slice(0, 20).map((r) => ({ value: r.value, visits: r.n, ai: aiEngine(r.value) })),
      utmCampaigns: of("utm_campaign").slice(0, 20).map((r) => ({ value: r.value, visits: r.n })),
      ctas, links,
    },
    linked: {
      visits: linked.visits, share: pct(linked.visits, anonVisits), signups: linked.signups, signupRate: pct(linked.signups, linked.visits),
      paths: linked.paths.map((p) => ({ path: p.more ? [...p.path, "…"] : p.path, signup: p.signup, visits: p.visits })),
      entries: linked.entries, exits: linked.exits,
    },
  };
}
